import { computed, type Ref } from "vue";
import {
  normalizePresentationAttributeName,
  TEMPLE_ATTRIBUTE_NAMES,
} from "../../../../../utils/productPresentation";
import { isPublishableSku } from "../../../../../utils/productSku";

const normalizeAttributeName = (name: unknown) =>
  String(name ?? "")
    .replace(/:\s*$/, "")
    .trim();

const normalizeAttributeValue = (options: unknown) => {
  if (!Array.isArray(options)) {
    const value = String(options ?? "").trim();
    return value || "N/A";
  }

  const values = options
    .map((option: unknown) => String(option ?? "").trim())
    .filter(Boolean);

  return values.length ? values.join(", ") : "N/A";
};

// The label comes from the catalog, which still names the temple measurement
// "Temple width" (or "Frame length"). Under any of those names it is shown as
// "Temple length", so the rename in OpenCart changes nothing on the page.
const TEMPLE_LABEL = "Temple length";
const TEMPLE_NAMES = new Set(
  TEMPLE_ATTRIBUTE_NAMES.map(normalizePresentationAttributeName)
);
// The catalog's letter case is kept, so a "Temple Width" row stays where it
// was, next to "Lens Width", as "Temple Length".
const templeLabel = (name: string) =>
  /^\S+\s+[A-Z]/.test(name) ? "Temple Length" : TEMPLE_LABEL;

const FEATURED_ATTRIBUTE_NAMES = ["Lens width", "Bridge width", TEMPLE_LABEL];

// The UPC is read from the SEO bridge, which takes it from the product's own
// UPC field and keeps it only with a correct check digit: a 12-digit UPC-A or
// a 13-digit EAN-13, both of which the catalog calls UPC (an 8-digit UPC-E
// arrives expanded to its 12-digit UPC-A). Anything else in that field
// ("NaN", a wrong check digit) gives no UPC row.
const UPC_LENGTH: Record<string, number> = { UPC_A: 12, EAN_13: 13 };
const productUpc = (product: Record<string, any> | null | undefined) => {
  const merchant = product?.merchant;
  if (!merchant || !["READY", "PARTIAL"].includes(merchant.status)) return "";
  const upc = Array.isArray(merchant.gtins)
    ? merchant.gtins.find(
        (gtin: any) => gtin?.source === "UPC" && UPC_LENGTH[gtin?.type]
      )
    : undefined;
  const value = String(upc?.value ?? "").trim();
  return /^\d+$/.test(value) && value.length === UPC_LENGTH[upc?.type]
    ? value
    : "";
};

// Product details are shown in three groups, in the owner's order. A row
// goes to the first group whose pattern matches its name; the frame
// measurements (shown here only when the catalog names them in Title Case)
// belong to the frame, and anything unknown to Product information. Each
// group keeps the rows' own order, so UPC still follows Made in and SKU is
// last.
export type DetailGroupKey = "info" | "lens" | "frame";
const FRAME_ROW =
  /^(frame (shape|colou?r|material|length)|adjustable nosepads|rx[\s-]?able|lens width|bridge width|temple (length|width))$/i;
const LENS_ROW = /^(lens (type|colou?r|material|technology)|polarized)$/i;
const DETAIL_GROUPS: Array<{ key: DetailGroupKey; title: string }> = [
  { key: "info", title: "Product information" },
  { key: "frame", title: "Frame details" },
  { key: "lens", title: "Lens details" },
];

export function groupProductDetailRows<T extends { name: string }>(rows: T[]) {
  const groupOf = (name: string): DetailGroupKey => {
    const label = String(name ?? "")
      .trim()
      .replace(/\s+/g, " ");
    if (FRAME_ROW.test(label)) return "frame";
    if (LENS_ROW.test(label)) return "lens";
    return "info";
  };
  return DETAIL_GROUPS.map((group) => ({
    ...group,
    rows: rows.filter((row) => groupOf(row.name) === group.key),
  })).filter((group) => group.rows.length > 0);
}

export function useProductAttributes(product: Ref<Record<string, any> | null>) {
  const productDetails = computed(() => {
    if (!Array.isArray(product.value?.attributes)) {
      return [];
    }

    // The first temple attribute is the one the size block and the Size row
    // read; a later one is dropped, as there, so a product never shows two
    // temple rows or a leftover "Temple width".
    let templeFound = false;
    return product.value.attributes
      .flatMap((attribute: any) => {
        const name = normalizeAttributeName(attribute?.name);
        const value = normalizeAttributeValue(attribute?.options);
        if (!TEMPLE_NAMES.has(normalizePresentationAttributeName(name))) {
          return [{ name, value }];
        }
        if (templeFound) return [];
        templeFound = true;
        return [{ name: templeLabel(name), value }];
      })
      .filter((attribute: any) => attribute.name && attribute.name !== "Size");
  });

  const featuredProductDetails = computed(() =>
    productDetails.value.filter((attribute: any) =>
      FEATURED_ATTRIBUTE_NAMES.includes(attribute.name)
    )
  );

  const productDetailRows = computed(() => {
    const rows = productDetails.value.filter(
      (attribute: any) =>
        ![...FEATURED_ATTRIBUTE_NAMES, "Type"].includes(attribute.name)
    );

    // Surface the manufacturer model number as "Model-code", placed directly
    // above "Color-code". Skipped when the backend already provides one or
    // when product.model is empty.
    const modelCode = String(product.value?.model ?? "").trim();
    const hasModelCode = rows.some((r: any) =>
      /^model[\s-]?code$/i.test(r.name)
    );
    if (modelCode && !hasModelCode) {
      const entry = { name: "Model-code", value: modelCode };
      const colorIdx = rows.findIndex((r: any) =>
        /^colou?r[\s-]?code$/i.test(r.name)
      );
      if (colorIdx >= 0) rows.splice(colorIdx, 0, entry);
      else rows.unshift(entry);
    }

    // UPC directly after "Made in" (at the end when there is no "Made in"),
    // so it always sits above SKU.
    const upc = productUpc(product.value);
    const hasUpc = rows.some((r: any) => /^upc$/i.test(r.name));
    if (upc && !hasUpc) {
      const entry = { name: "UPC", value: upc };
      const madeInIdx = rows.findIndex((r: any) => /^made\s+in$/i.test(r.name));
      if (madeInIdx >= 0) rows.splice(madeInIdx + 1, 0, entry);
      else rows.push(entry);
    }

    // SKU pinned to the very bottom of the list. A null or zero SKU is no
    // fact (utils/productSku), so the row is left out.
    const rawSku = product.value?.sku;
    const sku = isPublishableSku(rawSku) ? String(rawSku).trim() : "";
    const hasSku = rows.some((r: any) => /^sku$/i.test(r.name));
    if (sku && !hasSku) rows.push({ name: "SKU", value: sku });

    return rows;
  });

  return {
    featuredProductDetails,
    productDetailRows,
  };
}
